// Auto-saves in-progress note edits to localStorage so a session timeout / reload never loses
// an unsaved note. Edits arrive as react-redux-form FORM_CHANGE actions whose `model` is the
// store path being edited (e.g. `entities.dossierItems.byId.<id>.text`); we debounce per note
// and snapshot the whole record from the store. This follows the same redux-logic pattern as
// logic/invoices/paid.js (listen to FORM_CHANGE, filter by `action.model`) — no bespoke
// middleware needed. We keep our own per-note debounce (rather than redux-logic's global
// `debounce` option) so unrelated form edits can't starve the save and so flushDrafts can
// force a pending write before the store is wiped on session expiry.

import debounce from 'debounce'
import { createLogic } from 'redux-logic'
import formActions from 'react-redux-form/action-types'
import get from 'react-redux-form/utils/get'
import { clearDraft, saveDraft } from 'drafts'
import { getCurrentEmployment } from 'selectors/employments'

// Matches the entity path within a form model, e.g.
// `entities.dossierItems.byId.123.text` -> `entities.dossierItems.byId.123`.
const TRACKED_PATH = /^(entities\.dossierItems?\.byId\.[^.]+)/
const DEBOUNCE_MS = 1000

const savers = {}        // entityPath -> debounced save fn
const pending = new Set() // entity paths touched since their last save
const discarded = new Set() // entity paths whose form was closed — ignore until it remounts
let latestGetState = null


function saveNow(entityPath) {
  pending.delete(entityPath)
  if (discarded.has(entityPath)) return
  if (!latestGetState) return
  const state = latestGetState()
  const employment = getCurrentEmployment(state)
  const record = get(state, entityPath)
  if (employment?.draftEncryptionKey && record) {
    return saveDraft(entityPath, employment, record)
  }
}

const draftPersistLogic = createLogic({
  type: formActions.CHANGE,
  process ({ getState, action }, dispatch, done) {
    const match = typeof action.model === 'string' && TRACKED_PATH.exec(action.model)
    if (match) {
      const entityPath = match[1]
      // A closed form marks its entity discarded; ignore stray changes (e.g. the editor's
      // blur-commit fired by closing, which lands here *after* the form unmounted) so we don't
      // resurrect a just-cleared draft.
      if (!discarded.has(entityPath)) {
        latestGetState = getState
        pending.add(entityPath)
        if (!savers[entityPath]) savers[entityPath] = debounce(() => saveNow(entityPath), DEBOUNCE_MS)
        savers[entityPath]()
      }
    }
    done()
  }
})

// Persist any pending (debounced) draft writes immediately — used before the store is wiped on
// session expiry so the latest keystrokes aren't lost to the debounce window. Encryption is
// async, so this returns a promise the caller can await before wiping/redirecting.
export function flushDrafts () {
  return Promise.all(Array.from(pending).map((entityPath) => {
    if (savers[entityPath]) savers[entityPath].cancel()
    return saveNow(entityPath)
  }))
}

// Called when a note form is closed: the user is done with this draft. Cancel any pending
// autosave, remove the stored copy, and mark the entity discarded so a stray FORM_CHANGE from
// the close itself (the editor's blur-commit, which redux-logic processes *after* this runs)
// doesn't re-create the draft. resumeDraft re-enables it when the form mounts again.
export function discardDraft (modelPath, employment) {
  const match = typeof modelPath === 'string' && TRACKED_PATH.exec(modelPath)
  if (!match) return
  const entityPath = match[1]
  discarded.add(entityPath)
  if (savers[entityPath]) savers[entityPath].cancel()
  pending.delete(entityPath)
  clearDraft(entityPath, employment)
}

// Re-enable autosaving for an entity (called when its form mounts) so re-editing an existing
// note after a previous discard saves again.
export function resumeDraft (modelPath) {
  const match = typeof modelPath === 'string' && TRACKED_PATH.exec(modelPath)
  if (match) discarded.delete(match[1])
}

export default [draftPersistLogic]
